import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { Building2, Plus, Calendar, Users, MapPin, Edit2, XCircle, AlertTriangle, RefreshCw, X, List, Copy, History, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate, formatTimeRange } from '../../utils/dateTime';
import DonationModal from '../../components/DonationModal';
import CalendarView from '../../components/CalendarView';

const REPEAT_OPTIONS = [
  { value: 'none', label: 'No (One-time)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
];

const REPEAT_BADGE = {
  daily: 'bg-blue-50 text-blue-700',
  weekly: 'bg-purple-50 text-purple-700',
  biweekly: 'bg-indigo-50 text-indigo-700',
  monthly: 'bg-teal-50 text-teal-700',
};

const PartnerOpportunities = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [lastCreatedTitle, setLastCreatedTitle] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [timeFilter, setTimeFilter] = useState('upcoming'); // 'upcoming', 'past', 'archived'
  const [donationInfo, setDonationInfo] = useState({
    donation_url: null,
    donation_qr_image: null,
    suggested_amounts: [],
    is_available: false
  });
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    capacity: 10,
    credit_hours: 2,
    town: '',
    categories: [],
    duration_minutes: 120,
    is_flexible_scheduling: false,
    host_contact: '',
    repeat_schedule: 'none',
    status: 'open'
  });

  const fetchDonationInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/donation/partner`);
      setDonationInfo(response.data || { is_available: false });
    } catch (err) {
      console.error('fetchDonationInfo failed', err);
    }
  }, []);

  const [calendarOpportunities, setCalendarOpportunities] = useState([]);
  const fetchOpportunities = useCallback(async () => {
    try {
      // List honors timeFilter; calendar always shows the full picture
      // (past + upcoming + archived) so historical activity stays visible.
      const [listRes, calRes] = await Promise.all([
        axios.get(`${API}/opportunities?view=${timeFilter}`),
        axios.get(`${API}/opportunities?view=all`),
      ]);
      setOpportunities(listRes.data || []);
      setCalendarOpportunities(calRes.data || []);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, [timeFilter]);

  useEffect(() => {
    fetchOpportunities();
    fetchDonationInfo();
  }, [fetchOpportunities, fetchDonationInfo]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: (prev.categories || []).includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...(prev.categories || []), category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOpp) {
        try {
          await axios.put(`${API}/opportunities/${editingOpp.id}`, formData);
          toast.success('Opportunity updated');
        } catch (err) {
          // Phase 5: capacity-conflict — offer override
          const detail = err.response?.data?.detail || '';
          if (err.response?.status === 400 && detail.includes('Cannot lower capacity')) {
            if (window.confirm(`${detail}\n\nDo you want to proceed anyway and keep all current signups?`)) {
              await axios.put(`${API}/opportunities/${editingOpp.id}`, { ...formData, allow_overcapacity: true });
              toast.success('Opportunity updated (capacity override applied)');
            } else {
              return;
            }
          } else {
            throw err;
          }
        }
        setEditingOpp(null);
      } else {
        await axios.post(`${API}/opportunities`, formData);
        toast.success('Opportunity created');
        setLastCreatedTitle(formData.title);
        setShowDonationModal(true);
      }
      setShowCreateModal(false);
      fetchOpportunities();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save opportunity');
    }
  };

  // Phase 1: Duplicate Opportunity
  const handleDuplicate = async (opp, e) => {
    e.stopPropagation();
    if (!window.confirm(`Duplicate "${opp.title}"?\n\nThis creates a new, empty opportunity with the same details — no signups, attendance, or hours are copied.`)) return;
    try {
      const res = await axios.post(`${API}/opportunities/${opp.id}/duplicate`);
      toast.success(`Duplicated as "${res.data.title}"`);
      fetchOpportunities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate opportunity');
    }
  };

  // Archive / Unarchive (preserves all related data)
  const handleArchive = async (opp, e) => {
    e.stopPropagation();
    const verb = opp.is_archived ? 'Restore' : 'Archive';
    if (!window.confirm(`${verb} "${opp.title}"?\n\nThis is a UI-only change — all signups, attendance, hours and history are preserved.`)) return;
    try {
      await axios.put(`${API}/opportunities/${opp.id}/${opp.is_archived ? 'unarchive' : 'archive'}`);
      toast.success(opp.is_archived ? 'Opportunity restored' : 'Opportunity archived');
      fetchOpportunities();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb.toLowerCase()}`);
    }
  };

  // Phase 3: Edit history
  const [historyOpp, setHistoryOpp] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const handleViewHistory = async (opp, e) => {
    e.stopPropagation();
    setHistoryOpp(opp);
    setHistoryLoading(true);
    setHistoryEntries([]);
    try {
      const res = await axios.get(`${API}/opportunities/${opp.id}/history`);
      setHistoryEntries(res.data.history || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEdit = (opp, e) => {
    e.stopPropagation();
    setEditingOpp(opp);
    setFormData({
      title: opp.title || '',
      date: opp.date || '',
      start_time: opp.start_time || '',
      end_time: opp.end_time || '',
      location: opp.location || '',
      description: opp.description || '',
      capacity: opp.capacity || 10,
      credit_hours: opp.credit_hours || 2,
      town: opp.town || '',
      categories: opp.categories || [],
      duration_minutes: opp.duration_minutes || 120,
      is_flexible_scheduling: opp.is_flexible_scheduling || false,
      host_contact: opp.host_contact || '',
      repeat_schedule: opp.repeat_schedule || 'none',
      status: opp.status || 'open'
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      description: '',
      capacity: 10,
      credit_hours: 2,
      town: '',
      categories: [],
      duration_minutes: 120,
      is_flexible_scheduling: false,
      host_contact: '',
      repeat_schedule: 'none',
      status: 'open'
    });
    setEditingOpp(null);
  };

  return (
    <div className="min-h-screen bg-[#FBF4EA]">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div>
                <div className="font-bold text-lg text-[#222222]">{user?.partner_org_name || 'Partner Portal'}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/partner/dashboard" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#8B2E3C] font-bold">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Participants</Link>
              <Link to="/partner/invites" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Invites</Link>
              <Link to="/partner/staff" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline text-sm">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">Manage Opportunities</h1>
            <p className="text-base text-[#666666]">Create and manage volunteer opportunities</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Opportunity
          </button>
        </div>

        {/* View Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="flex bg-white rounded-lg border border-[#E9DDCF] overflow-hidden">
            <button onClick={() => setViewMode('list')} className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-[#8B2E3C] text-white' : 'text-[#666] hover:bg-[#FBF4EA]'}`} data-testid="view-list-btn">
              <List className="w-4 h-4" /> List
            </button>
            <button onClick={() => setViewMode('calendar')} className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${viewMode === 'calendar' ? 'bg-[#8B2E3C] text-white' : 'text-[#666] hover:bg-[#FBF4EA]'}`} data-testid="view-calendar-btn">
              <Calendar className="w-4 h-4" /> Calendar
            </button>
          </div>
          <div className="flex gap-2">
            {['upcoming', 'past', 'archived'].map(f => (
              <button key={f} onClick={() => setTimeFilter(f)} className={`px-3 py-1.5 text-sm rounded-lg ${timeFilter === f ? 'bg-[#C9A227] text-white' : 'bg-white text-[#666] border border-[#E9DDCF] hover:bg-[#FBF4EA]'}`} data-testid={`filter-${f}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Opportunities */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (() => {
          // Server-side already filters by ?view=, so no client-side date filter needed.
          const filtered = opportunities;

          // In calendar mode, use the full set (past + upcoming + archived) so
          // historical activity stays visible. Empty-state uses that set too.
          if (viewMode === 'calendar') {
            if (calendarOpportunities.length === 0) {
              return (
                <div className="card text-center py-12" data-testid="opportunities-empty">
                  <p className="text-[#666666] mb-4">No opportunities yet</p>
                  <button onClick={() => setShowCreateModal(true)} className="btn-primary">Create First Opportunity</button>
                </div>
              );
            }
            return <CalendarView opportunities={calendarOpportunities} basePath="/partner/opportunities" />;
          }

          if (filtered.length === 0 && timeFilter === 'upcoming') {
            return (
              <div className="card text-center py-12" data-testid="opportunities-empty">
                <p className="text-[#666666] mb-4">No upcoming opportunities yet</p>
                <button onClick={() => setShowCreateModal(true)} className="btn-primary">Create First Opportunity</button>
              </div>
            );
          }

          if (filtered.length === 0) {
            return <div className="card text-center py-8 text-[#666]" data-testid="opportunities-empty">No {timeFilter} opportunities found.</div>;
          }

          return (
            <div className="grid gap-4">
              {filtered.map(opp => {
              const oppDate = parseLocalDate(opp.date);
              const formattedDate = format(oppDate, 'MMM d, yyyy');
              const isRecentlyEdited = opp.last_edited_at &&
                (new Date() - new Date(opp.last_edited_at)) < 7 * 24 * 60 * 60 * 1000;
              return (
                <div key={opp.id} className="card hover:shadow-lg transition-shadow" onClick={() => navigate(`/partner/opportunities/${opp.id}`)}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-[#222222]">{opp.title}</h3>
                        {opp.repeat_schedule && opp.repeat_schedule !== 'none' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${REPEAT_BADGE[opp.repeat_schedule] || 'bg-gray-100 text-gray-600'}`}>
                            <RefreshCw className="w-3 h-3" />
                            {opp.repeat_schedule}
                          </span>
                        )}
                        {isRecentlyEdited && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800"
                            title={`Last edited ${format(new Date(opp.last_edited_at), 'MMM d, yyyy h:mm a')}${opp.last_edited_by_name ? ` by ${opp.last_edited_by_name}` : ''}`}
                            data-testid={`updated-badge-${opp.id}`}
                          >
                            Updated
                          </span>
                        )}
                        {opp.is_completed && !opp.is_archived && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800" data-testid={`completed-badge-${opp.id}`}>
                            Completed
                          </span>
                        )}
                        {opp.is_archived && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-700" data-testid={`archived-badge-${opp.id}`}>
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#666666] mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formattedDate} {opp.start_time && `• ${formatTimeRange(opp.start_time, opp.end_time)}`}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {opp.town || opp.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {opp.signups_count} / {opp.capacity}
                        </div>
                      </div>
                      {opp.pending_signups_count > 0 && (
                        <span className="badge badge-pending">{opp.pending_signups_count} pending approval</span>
                      )}
                      {isRecentlyEdited && opp.last_changed_fields?.length > 0 && (
                        <p className="text-xs text-blue-700 mt-1.5" data-testid={`changed-fields-${opp.id}`}>
                          Recently changed: {opp.last_changed_fields.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={(e) => handleEdit(opp, e)}
                        className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                        title="Edit opportunity"
                        data-testid={`edit-opp-${opp.id}`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => handleDuplicate(opp, e)}
                        className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                        title="Duplicate opportunity"
                        data-testid={`duplicate-opp-${opp.id}`}
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => handleViewHistory(opp, e)}
                        className="p-2 text-[#666] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                        title="Edit history"
                        data-testid={`history-opp-${opp.id}`}
                      >
                        <History className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => handleArchive(opp, e)}
                        className="p-2 text-[#666] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                        title={opp.is_archived ? 'Restore (unarchive)' : 'Archive'}
                        data-testid={`archive-opp-${opp.id}`}
                      >
                        {opp.is_archived
                          ? <ArchiveRestore className="w-5 h-5" />
                          : <Archive className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#222222]">
                  {editingOpp ? 'Edit Opportunity' : 'Create Opportunity'}
                </h2>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-[#666] hover:text-[#222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Title *</label>
                    <input type="text" name="title" className="input-field" value={formData.title} onChange={handleInputChange} required data-testid="opp-title-input" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Date *</label>
                    <input type="date" name="date" className="input-field" value={formData.date} onChange={handleInputChange} required data-testid="opp-date-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Time *</label>
                      <input type="time" name="start_time" className="input-field" value={formData.start_time} onChange={handleInputChange} required={!formData.is_flexible_scheduling} disabled={formData.is_flexible_scheduling} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Time *</label>
                      <input type="time" name="end_time" className="input-field" value={formData.end_time} onChange={handleInputChange} required={!formData.is_flexible_scheduling} disabled={formData.is_flexible_scheduling} />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <input type="checkbox" id="is_flexible_scheduling" name="is_flexible_scheduling" checked={formData.is_flexible_scheduling} onChange={handleInputChange} className="w-4 h-4" />
                      <label htmlFor="is_flexible_scheduling" className="text-sm font-medium">Flexible Scheduling</label>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Location *</label>
                    <input type="text" name="location" className="input-field" value={formData.location} onChange={handleInputChange} required placeholder="e.g., Main Street Park" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Town</label>
                    <input type="text" name="town" className="input-field" value={formData.town} onChange={handleInputChange} placeholder="e.g., Ossipee" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Capacity *</label>
                    <input type="number" name="capacity" className="input-field" value={formData.capacity} onChange={handleInputChange} required min="1" />
                  </div>

                  {/* Repeat Schedule */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Repeat?</label>
                    <select
                      name="repeat_schedule"
                      className="input-field"
                      value={formData.repeat_schedule}
                      onChange={handleInputChange}
                      data-testid="repeat-schedule-select"
                    >
                      {REPEAT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Status (only in edit mode) */}
                  {editingOpp && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        name="status"
                        className="input-field"
                        value={formData.status}
                        onChange={handleInputChange}
                        data-testid="opp-status-select"
                      >
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  )}

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Categories</label>
                    <div className="flex flex-wrap gap-2">
                      {['Serving Food', 'Cleanup', 'Childcare', 'Admin', 'Outdoor Labor', 'Other'].map(cat => (
                        <button key={cat} type="button" onClick={() => handleCategoryToggle(cat)} className={`px-3 py-1 rounded-lg text-sm transition-colors ${(formData.categories || []).includes(cat) ? 'bg-[#8B2E3C] text-white' : 'bg-[#E9DDCF] text-[#222222] hover:bg-[#C9A227]'}`}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Supervisor Contact *</label>
                    <input type="text" name="host_contact" className="input-field" value={formData.host_contact} onChange={handleInputChange} required placeholder="Name + Phone/Email" />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <textarea name="description" className="input-field" rows="4" value={formData.description} onChange={handleInputChange} required placeholder="Describe what volunteers will do..." />
                  </div>
                </div>

                {editingOpp && (
                  <p className="text-xs text-[#8B2E3C] bg-[#FBF4EA] border border-[#E9DDCF] rounded-lg px-3 py-2 mb-3" data-testid="partner-notification-banner">
                    Signed-up participants will be notified by email only for major changes: date, start/end time, or location. Minor edits won't trigger emails.
                  </p>
                )}
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="btn-outline">Cancel</button>
                  <button type="submit" className="btn-primary" data-testid="save-opp-button">
                    {editingOpp ? 'Update & Notify' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Donation Modal */}
      <DonationModal
        isOpen={showDonationModal}
        onClose={() => setShowDonationModal(false)}
        donationUrl={donationInfo.donation_url}
        donationQrImage={donationInfo.donation_qr_image}
        suggestedAmounts={donationInfo.suggested_amounts || []}
        isAvailable={donationInfo.is_available}
        opportunityTitle={lastCreatedTitle}
      />

      {/* Phase 3: Edit History Modal */}
      {historyOpp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="history-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E9DDCF] flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-2xl font-bold text-[#222222]">Edit History</h2>
                <p className="text-sm text-[#666] mt-1">{historyOpp.title}</p>
              </div>
              <button
                onClick={() => { setHistoryOpp(null); setHistoryEntries([]); }}
                className="text-[#666] hover:text-[#222]"
                data-testid="close-history-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {historyLoading ? (
                <p className="text-center text-[#666] py-8">Loading...</p>
              ) : historyEntries.length === 0 ? (
                <p className="text-center text-[#666] py-8" data-testid="history-empty">No edits yet. Future edits will be tracked here.</p>
              ) : (
                <ol className="space-y-4" data-testid="history-list">
                  {historyEntries.map((entry, i) => (
                    <li key={entry.id || i} className="border-l-2 border-[#8B2E3C] pl-4 pb-2">
                      <div className="text-sm text-[#222] font-semibold">
                        {entry.changed_by_name || entry.changed_by_email || 'Unknown user'}
                        <span className="text-[#666] font-normal ml-2 text-xs">
                          ({entry.changed_by_role || 'user'}) • {entry.changed_at ? format(new Date(entry.changed_at), 'MMM d, yyyy h:mm a') : ''}
                        </span>
                      </div>
                      <ul className="mt-2 space-y-1">
                        {(entry.changes || []).map((c, j) => (
                          <li key={j} className="text-xs text-[#444] bg-[#FBF4EA] rounded px-2 py-1">
                            <span className="font-medium text-[#8B2E3C]">{c.field}</span>:
                            <span className="line-through text-[#999] ml-1">{c.old === null || c.old === undefined ? 'empty' : String(c.old).slice(0, 80)}</span>
                            <span className="mx-1">→</span>
                            <span className="text-[#222]">{c.new === null || c.new === undefined ? 'empty' : String(c.new).slice(0, 80)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerOpportunities;
